// Per-variety memory (Step 6.7 section 7). Writes after every successful
// Add, reads on every UI mount that renders a picker for a known variety.
//
// localStorage["mag-portal-prefs"] = JSON.stringify({
//   "rose-freedom": { color, length, grade, qty, unit, updatedAt },
//   "alstroemeria": { ... }
// })

const PREFS_KEY = "mag-portal-prefs";

export type VarietyPref = {
  color?: string;
  length?: number;
  grade?: string;
  qty: number;
  unit: "bunch" | "box";
  // DIRECTIVE #023: per-variety EB/QB pick. When the client picks a
  // non-default box size on the detail page (or QuickAddPopover or
  // mobile bottom sheet), we persist that choice here so PowerBuyerRow
  // also reflects it. Per Alexis lock today: this saved value WINS over
  // the new universal QB default.
  boxCode?: "EB" | "QB" | "HB";
  updatedAt: string; // ISO timestamp
};

type PrefsMap = Record<string, VarietyPref>;

function readAll(): PrefsMap {
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as PrefsMap;
    if (parsed && typeof parsed === "object") return parsed;
    return {};
  } catch {
    return {};
  }
}

function writeAll(prefs: PrefsMap): void {
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // ignore (quota / unavailable)
  }
}

export function readVarietyPref(slug: string): VarietyPref | null {
  if (typeof window === "undefined") return null;
  const all = readAll();
  return all[slug] ?? null;
}

export function writeVarietyPref(
  slug: string,
  pref: Omit<VarietyPref, "updatedAt"> & { updatedAt?: string },
): void {
  if (typeof window === "undefined") return;
  const all = readAll();
  all[slug] = {
    ...pref,
    updatedAt: pref.updatedAt ?? new Date().toISOString(),
  };
  writeAll(all);
}

export function clearAllVarietyPrefs(): void {
  try {
    window.localStorage.removeItem(PREFS_KEY);
  } catch {
    // ignore
  }
}

// Default helpers for callers that need a fallback shape when no
// stored preference exists. Keeping the spec-defined defaults centralized
// so QuickAddPopover, PowerBuyerRow, and DetailPricingPanel pick up the
// same values.
export type DefaultPickArgs = {
  hasColorPicker: boolean;
  isHydrangea: boolean;
  defaultLength: number | undefined;
  primaryColor: string;
  defaultGrade?: string;
  primaryColorIsMix?: boolean; // Power Buyer override
};

export function resolvePref(
  slug: string,
  args: DefaultPickArgs,
): VarietyPref {
  const saved = readVarietyPref(slug);
  if (saved) return saved;
  return {
    color: args.hasColorPicker
      ? args.primaryColorIsMix
        ? "mix"
        : args.primaryColor
      : undefined,
    length: args.isHydrangea ? undefined : args.defaultLength,
    grade: args.defaultGrade,
    qty: 1,
    // Step 6.7.1 section 4a: first-visit default unit is Box.
    unit: "box",
    updatedAt: new Date(0).toISOString(),
  };
}
